import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import Tab from "@mui/material/Tab"
import TableCell from "@mui/material/TableCell"
import Tabs from "@mui/material/Tabs"
import { format } from "date-fns"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import Column from "../../../common/components/Column"
import LengthLimitedTextField from "../../../common/components/LengthLimitedTextField"
import Row from "../../../common/components/Row"
import { useNavigateUpdatedSearchParams } from "../../../common/utils"
import AIGenButton, {
  useAIGenButtonState,
} from "../../customer/components/AIGenButton"
import AddAttachmentsSection, {
  useAddAttachmentSectionState,
} from "../../customer/components/AddAttachmentsSection"
import EmailWYSWIGEditor, {
  useEmailWYSWIGEditorState,
} from "../../customer/components/EmailWYSWIGEditor"
import { isEditorBodyEmpty } from "../../customer/components/emailSignatureUtils"
import { getEmailEditorHTML } from "../../customer/email-campaigns/edit-email-mixin"
import { AUTOCOMPLETE_OPTIONS } from "../../customer/utils"
import { adminLiquidTabsSx } from "../adminStyles"
import { adminApi } from "../axios"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListToolbar from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import GenerateInboxTemplatePromptPopover from "../components/GenerateInboxTemplatePromptPopover"
import InboxTemplateStatusChip from "../components/InboxTemplateStatusChip"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"
import { selectAdminProfile } from "../reducer"

// Approval status lives in the URL so "needs review" views can be shared.
const TEMPLATE_FILTER_SPECS = {
  status: { param: "approval_status" },
}

const TEMPLATE_TOOLBAR_FILTERS = [
  {
    key: "status",
    label: "Status",
    type: "select",
    anyLabel: "All statuses",
    minWidth: 180,
    options: [
      { value: "approved", label: "Approved" },
      { value: "pending", label: "Pending approval" },
      { value: "rejected", label: "Rejected" },
    ],
  },
]

// Tab is in the URL; switching drops the other tab's search/sort/filters.
const TEMPLATE_TAB_SCOPED_PARAMS = ["q", "pi", "sc", "sd", "status"]

function formatDate(value) {
  if (!value) return "—"
  return format(new Date(value), "MMM d, yyyy")
}

export default function AdminInboxTemplates() {
  const [searchParams] = useSearchParams()
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()
  const tab = searchParams.get("tab") === "concierge" ? 1 : 0

  function setTab(nextTab) {
    if (nextTab === tab) return
    const cleared = Object.fromEntries(
      TEMPLATE_TAB_SCOPED_PARAMS.map((param) => [param, null])
    )
    navigateUpdatedSearchParams({
      ...cleared,
      tab: nextTab === 1 ? "concierge" : null,
    })
  }
  const [creating, setCreating] = useState(false)

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Inbox templates"
        subtitle={
          tab === 0
            ? "Default templates every concierge can pick when replying to inbound mail. New templates stay hidden in CRM Settings until grace@smartsetter.io approves them."
            : "Account-specific email templates created by concierge users. Staff admins and super admins can approve them; the approver is recorded for audit."
        }
        actions={
          tab === 0 ? (
            <Button variant="contained" onClick={() => setCreating(true)}>
              New template
            </Button>
          ) : null
        }
      />
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={adminLiquidTabsSx}
      >
        <Tab label="Default templates" />
        <Tab label="Concierge templates" />
      </Tabs>
      {tab === 0 ? (
        <DefaultInboxTemplatesPanel
          creating={creating}
          onCreatingChange={setCreating}
        />
      ) : (
        <ConciergeInboxTemplatesPanel />
      )}
    </Column>
  )
}

function DefaultInboxTemplatesPanel({ creating, onCreatingChange }) {
  const profile = useSelector(selectAdminProfile)
  const canApprove = Boolean(profile?.can_approve_inbox_templates)
  const [editing, setEditing] = useState(null)
  const [deleteTemplate, setDeleteTemplate] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [actingId, setActingId] = useState(null)

  const query = useAdminPaginatedQuery(adminApi.fetchInboxTemplates, {
    defaultSortColumn: "name",
    defaultSortDirection: "asc",
    filters: TEMPLATE_FILTER_SPECS,
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    refresh,
  } = query

  async function handleDelete() {
    if (!deleteTemplate) return
    setDeleting(true)
    try {
      await adminApi.deleteInboxTemplate(deleteTemplate.id)
      setDeleteTemplate(null)
      refresh()
    } finally {
      setDeleting(false)
    }
  }

  async function handleApproval(template, action) {
    setActingId(template.id)
    try {
      if (action === "approve") {
        await adminApi.approveInboxTemplate(template.id)
      } else {
        await adminApi.rejectInboxTemplate(template.id)
      }
      refresh()
    } finally {
      setActingId(null)
    }
  }

  return (
    <Column gap={2}>
      <AdminListToolbar
        query={query}
        searchLabel="Search name or subject"
        noun={totalResultsSize === 1 ? "template" : "templates"}
        filters={TEMPLATE_TOOLBAR_FILTERS}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={6}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="name"
              label="Name"
              query={query}
              minWidth={{ xs: 96, sm: 140 }}
            />
            <AdminSortableHeaderCell
              columnName="subject"
              label="Subject"
              query={query}
              minWidth={{ xs: 96, sm: 160 }}
            />
            <AdminSortableHeaderCell
              columnName="approval_status"
              label="Status"
              query={query}
              minWidth={{ xs: 72, sm: 96 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 56, sm: 80 }}>
              Attachments
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="created"
              label="Created"
              query={query}
              minWidth={{ xs: 72, sm: 96 }}
            />
            <AdminHeaderTableCell align="right" minWidth={{ xs: 96, sm: 120 }}>
              Actions
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((template) => (
          <AdminTableRow key={template.id} hover>
            <AdminTruncatedCell value={template.name} limit={32} />
            <AdminTruncatedCell value={template.subject} limit={40} />
            <TableCell sx={adminTableBodyCellSx}>
              <InboxTemplateStatusChip status={template.approval_status} />
            </TableCell>
            <AdminTruncatedCell
              value={
                template.attachments?.length ?? template.attachment_count ?? 0
              }
              align="right"
            />
            <AdminTruncatedCell value={formatDate(template.created)} />
            <TableCell align="right" sx={adminTableBodyCellSx}>
              <Row gap={0.5} justifyContent="flex-end">
                {canApprove && template.approval_status === "pending" && (
                  <>
                    <IconButton
                      size="small"
                      aria-label="Approve template"
                      disabled={actingId === template.id}
                      onClick={() => handleApproval(template, "approve")}
                    >
                      <CheckOutlinedIcon fontSize="small" color="success" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Reject template"
                      disabled={actingId === template.id}
                      onClick={() => handleApproval(template, "reject")}
                    >
                      <CloseOutlinedIcon fontSize="small" color="error" />
                    </IconButton>
                  </>
                )}
                <IconButton
                  size="small"
                  aria-label="Edit template"
                  onClick={() => setEditing(template)}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Delete template"
                  onClick={() => setDeleteTemplate(template)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Row>
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>

      <InboxTemplateDialog
        open={creating || Boolean(editing)}
        template={editing}
        onClose={() => {
          onCreatingChange(false)
          setEditing(null)
        }}
        onSaved={() => {
          onCreatingChange(false)
          setEditing(null)
          refresh()
        }}
      />

      <Dialog
        open={Boolean(deleteTemplate)}
        onClose={() => setDeleteTemplate(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete inbox template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete default template <strong>{deleteTemplate?.name}</strong>?
            Concierges will no longer see it when replying to inbound mail.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDeleteTemplate(null)}>
            Cancel
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={deleting}
            onClick={handleDelete}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Column>
  )
}

function ConciergeInboxTemplatesPanel() {
  const profile = useSelector(selectAdminProfile)
  const canApprove = Boolean(profile?.is_staff || profile?.is_superuser)
  const [actingId, setActingId] = useState(null)

  const query = useAdminPaginatedQuery(adminApi.fetchConciergeInboxTemplates, {
    defaultSortColumn: "created",
    defaultSortDirection: "desc",
    filters: TEMPLATE_FILTER_SPECS,
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    refresh,
  } = query

  async function handleApproval(template, action) {
    setActingId(template.id)
    try {
      if (action === "approve") {
        await adminApi.approveConciergeInboxTemplate(template.id)
      } else {
        await adminApi.rejectConciergeInboxTemplate(template.id)
      }
      refresh()
    } finally {
      setActingId(null)
    }
  }

  return (
    <Column gap={2}>
      <AdminListToolbar
        query={query}
        searchLabel="Search name, subject, or parent"
        noun={totalResultsSize === 1 ? "template" : "templates"}
        filters={TEMPLATE_TOOLBAR_FILTERS}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={7}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="name"
              label="Name"
              query={query}
              minWidth={{ xs: 96, sm: 140 }}
            />
            <AdminSortableHeaderCell
              columnName="subject"
              label="Subject"
              query={query}
              minWidth={{ xs: 96, sm: 160 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 160 }}>
              Parent account
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="approval_status"
              label="Status"
              query={query}
              minWidth={{ xs: 72, sm: 96 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 140 }}>
              Approved by
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="approved_at"
              label="Approved"
              query={query}
              minWidth={{ xs: 72, sm: 96 }}
            />
            <AdminHeaderTableCell align="right" minWidth={{ xs: 72, sm: 96 }}>
              Actions
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((template) => (
          <AdminTableRow key={template.id} hover>
            <AdminTruncatedCell value={template.name} limit={32} />
            <AdminTruncatedCell value={template.subject} limit={40} />
            <AdminTruncatedCell
              value={
                template.parent_name
                  ? `${template.parent_name} (${template.parent_email || ""})`
                  : template.parent_email
              }
              limit={40}
            />
            <TableCell sx={adminTableBodyCellSx}>
              <InboxTemplateStatusChip status={template.approval_status} />
            </TableCell>
            <AdminTruncatedCell value={template.approved_by_email} limit={32} />
            <AdminTruncatedCell value={formatDate(template.approved_at)} />
            <TableCell align="right" sx={adminTableBodyCellSx}>
              {canApprove && template.approval_status === "pending" ? (
                <Row gap={0.5} justifyContent="flex-end">
                  <IconButton
                    size="small"
                    aria-label="Approve template"
                    disabled={actingId === template.id}
                    onClick={() => handleApproval(template, "approve")}
                  >
                    <CheckOutlinedIcon fontSize="small" color="success" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Reject template"
                    disabled={actingId === template.id}
                    onClick={() => handleApproval(template, "reject")}
                  >
                    <CloseOutlinedIcon fontSize="small" color="error" />
                  </IconButton>
                </Row>
              ) : null}
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>
    </Column>
  )
}

function InboxTemplateDialog({ open, template, onClose, onSaved }) {
  const [name, setName] = useState("")
  const [subject, setSubject] = useState("")
  const [saving, setSaving] = useState(false)
  const [usedAI, setUsedAI] = useState(false)
  const [aiPrompt, setAiPrompt] = useState("")
  const { aiGenerateButtonRef, setAIGenerateButtonRef } = useAIGenButtonState()
  const {
    editorReady,
    setEditorReady,
    insertLinkDialogOpen,
    setInsertLinkDialogOpen,
    editorRef,
    setEditorBody,
  } = useEmailWYSWIGEditorState(open)
  const {
    fileAttachments,
    setFileAttachments,
    fileAttachmentError,
    setFileAttachmentError,
    setDeletedAttachments,
    addAttachmentsToFormData,
    convertBackendAttachmentsToFileAttachments,
  } = useAddAttachmentSectionState()

  useEffect(() => {
    if (!open) return
    setName(template?.name || "")
    setSubject(template?.subject || "")
    setUsedAI(false)
    setAiPrompt(template?.ai_prompt || "")
    setDeletedAttachments([])
  }, [open, template, setDeletedAttachments])

  useEffect(() => {
    if (!open || !editorReady) return
    setEditorBody(template?.html_body || "", false, "")
    convertBackendAttachmentsToFileAttachments(template?.attachments || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editorReady, template?.id])

  function handleGenerated(generated) {
    setAIGenerateButtonRef(null)
    if (!generated) return
    setUsedAI(true)
    setAiPrompt(generated.prompt || "")
    if (generated.subject) {
      setName((current) => current || generated.subject)
      setSubject(generated.subject)
    }
    if (generated.content) {
      const html = generated.content.replace(/\r?\n/g, "<br/>")
      setEditorBody(html, false, "")
    }
  }

  async function handleSave() {
    const html = editorRef.current ? getEmailEditorHTML(editorRef) : ""
    if (!name.trim() || isEditorBodyEmpty(html)) return
    const formData = new FormData()
    formData.append("name", name.trim())
    formData.append("subject", subject.trim() || name.trim())
    formData.append("html_body", html)
    formData.append("ai_generated", usedAI ? "true" : "false")
    formData.append("ai_prompt", aiPrompt)
    addAttachmentsToFormData(formData)
    setSaving(true)
    try {
      if (template) {
        await adminApi.updateInboxTemplate(template.id, formData)
      } else {
        await adminApi.createInboxTemplate(formData)
      }
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  const canSave = Boolean(name.trim()) && editorReady
  const saveLabel = template
    ? "Save and submit for approval"
    : "Submit for approval"

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>
        {template ? "Edit inbox template" : "New inbox template"}
      </DialogTitle>
      <DialogContent>
        <Column sx={{ gap: 2, pt: 1 }}>
          {!template && (
            <Alert severity="info">
              This template will stay pending until grace@smartsetter.io
              approves it. Only then does it appear in CRM Settings as a default
              inbox template.
            </Alert>
          )}
          {template?.approval_status === "pending" && (
            <Alert severity="warning">
              Waiting for approval from grace@smartsetter.io. Concierges cannot
              use this template until it is approved.
            </Alert>
          )}
          {template?.approval_status === "rejected" && (
            <Alert severity="error">
              This template was rejected. Edit it and submit again for approval.
            </Alert>
          )}
          <Row sx={{ gap: 1, alignItems: "flex-start" }}>
            <LengthLimitedTextField
              label="Template name"
              value={name}
              setValue={setName}
              maxLength={128}
              required
            />
            <AIGenButton
              campaign={null}
              setAIGenerateButtonRef={setAIGenerateButtonRef}
            />
            <GenerateInboxTemplatePromptPopover
              anchorElem={aiGenerateButtonRef}
              onClose={handleGenerated}
              generateFn={adminApi.generateInboxTemplate}
            />
          </Row>
          <LengthLimitedTextField
            label="Subject"
            helperText="Used to identify this template. Replies still keep the original thread subject."
            value={subject}
            setValue={setSubject}
            maxLength={988}
          />
          <EmailWYSWIGEditor
            height={280}
            editorRef={editorRef}
            editorReady={editorReady}
            setEditorReady={setEditorReady}
            insertLinkDialogOpen={insertLinkDialogOpen}
            setInsertLinkDialogOpen={setInsertLinkDialogOpen}
            onSetup={(editor) =>
              editor.ui.registry.addMenuButton("insert-agent-data", {
                text: "Insert placeholder",
                fetch: (callback) => {
                  callback(
                    AUTOCOMPLETE_OPTIONS.map((option) => ({
                      type: "menuitem",
                      text: option.label,
                      onAction: () =>
                        editor.insertContent(`{{ ${option.fieldName} }}`),
                    }))
                  )
                },
              })
            }
          />
          <AddAttachmentsSection
            fileAttachments={fileAttachments}
            setFileAttachments={setFileAttachments}
            fileAttachmentError={fileAttachmentError}
            setFileAttachmentError={setFileAttachmentError}
            setDeletedAttachments={setDeletedAttachments}
            maxAttachmentCount={null}
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          disabled={!canSave}
          onClick={handleSave}
        >
          {saveLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
